import { Badge } from '@/components/badge';
import Card, {
  CardContent,
  CardHeader,
  CardPlaceholder
} from '@/components/card';
import DataTable, {
  DataTableMenuCell,
  DataTableStackCell
} from '@/components/data-table/DataTable.component';
import { Link } from '@/components/link';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { useDisbursementsQuery } from '@/hooks/suba/useDisbursementsQuery.hook';
import { useUrlState } from '@/hooks/useUrlState.hook';
import { DisbursementDto } from '@/models/suba/disbursements/DisbursementDTO.model';
import { DisbursementSearchRequest } from '@/models/suba/disbursements/DisbursementSearchRequest.model';
import { DisbursementRequestQueueStatusColorMap } from '@/models/suba/distributions/DisbursementRequestQueueStatusColorMap.model';
import { ActionConfirmationDialog } from '@/routes/suba/accounts/common/components/dialogs/ActionConfirmationDialog.component';
import { DisbursementService } from '@/services/suba/disbursement/Disbursement.service';
import formatters from '@/utils/Formatters';
import SearchIcon from '@mui/icons-material/Search';
import { FormControlLabel, MenuItem, Stack, Switch } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CustodianId,
  DisbursementMethod,
  DisbursementStatus
} from '@vestwell-sub-accounting/models';

import { ColDef } from 'ag-grid-community';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useToggle } from 'react-use';

type DisbursementsTabProps = {
  accountId: string;
};

export const DisbursementsTab: FC<DisbursementsTabProps> = props => {
  const { showSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  // state
  const [disbursementSelected, setDisbursementSelected] =
    useState<DisbursementDto | null>(null);
  const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] =
    useToggle(false);
  const [isInactiveDisplay, toggleInactiveDisplay] = useToggle(false);

  const [urlState, setUrlState] = useUrlState<
    Pick<
      DisbursementSearchRequest,
      'orderBy' | 'orderByDirection' | 'page' | 'pageSize' | 'includeInactive'
    >
  >({
    includeInactive: false,
    orderBy: 'createdAt',
    orderByDirection: 'desc',
    page: 1,
    pageSize: 10
  });

  const disbursementsQuery = useDisbursementsQuery({
    ...urlState,
    parentAccountId: props.accountId
  });

  // callbacks

  useEffect(() => {
    setUrlState(prevUrlState => ({
      ...prevUrlState,
      includeInactive: isInactiveDisplay
    }));
  }, [isInactiveDisplay]);

  const handleDisbursmentsPageChange = useCallback(newPage => {
    setUrlState(prevUrlState => ({
      ...prevUrlState,
      page: newPage
    }));
  }, []);

  const handleDisbursmentsPageSizeChange = useCallback(newPageSize => {
    setUrlState(prevUrlState => ({
      ...prevUrlState,
      pageSize: newPageSize
    }));
  }, []);

  const handleDisbursmentsSortChange = useCallback(newSort => {
    setUrlState(prevUrlState => ({
      ...prevUrlState,
      ...newSort,
      page: 1
    }));
  }, []);

  const initiateStopCheckDisbursementRequest = useMutation({
    mutationFn: (disbursement: DisbursementDto) =>
      DisbursementService.initiateStopCheck(disbursement.id),
    mutationKey: ['DisbursementService.initiateStopCheck'],
    onError: (error: AxiosError<string>) => {
      showSnackbar({
        message: error.response?.data ?? error.message,
        severity: 'error'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['DisbursementsService.search']
      });
      showSnackbar({
        message: 'Disbursement stop check initiated',
        severity: 'success'
      });
    }
  });

  const columnDefs = useMemo(
    () =>
      [
        {
          field: 'id',
          headerName: 'Disbursement ID',
          sortable: true,
          type: 'text'
        },
        {
          cellRenderer: (cellData: { data: DisbursementDto }) => {
            return (
              <DataTableStackCell
                primary={cellData.data.disbursementMethod}
                secondary={
                  cellData.data.disbursementMethod === DisbursementMethod.Check
                    ? `Check number: ${cellData.data.custodianCheckNumber}`
                    : ''
                }
              />
            );
          },
          field: 'disbursementMethod',
          sortable: true,
          type: 'text'
        },
        {
          autoHeight: true,
          cellRenderer: (cellData: { data: DisbursementDto }) => {
            return (
              <Badge
                color={
                  DisbursementRequestQueueStatusColorMap[cellData.data?.status]
                }
                maxWidth='fit-content'>
                {formatters.displayCase(cellData.data?.status)}
              </Badge>
            );
          },
          field: 'status',
          sortable: true,
          type: 'text'
        },
        {
          field: 'custodianId',
          sortable: true,
          type: 'text'
        },
        {
          field: 'amount',
          sortable: true,
          type: 'numericColumn',
          valueFormatter: ({ data }) =>
            data !== null && data !== undefined
              ? formatters.formatDollars(data.amount)
              : ''
        },
        {
          field: 'createdAt',
          headerName: 'Init Date',
          valueFormatter: ({ data }) =>
            data !== null && data !== undefined
              ? dayjs(data.createdAt).format('MM/DD/YYYY hh:mm')
              : ''
        },
        {
          cellRenderer: (cellData: { data: DisbursementDto }) => {
            const tracerIdSelected =
              cellData.data.sourceTracerId || cellData.data.tracerId;
            return (
              <Link
                target='_blank'
                to={`/ops/workflows/tracer:${tracerIdSelected}`}>
                {tracerIdSelected}
              </Link>
            );
          },
          field: 'tracerId'
        },
        {
          cellRenderer: (cellData: { data: DisbursementDto }) => {
            return (
              <DataTableMenuCell>
                {cellData.data.custodianId === CustodianId.bnyTreasury &&
                  cellData.data.disbursementMethod ===
                    DisbursementMethod.Check &&
                  cellData.data.disbursementStatus ===
                    DisbursementStatus.OUTSTANDING && (
                    <MenuItem
                      onClick={() => {
                        setDisbursementSelected(cellData.data);
                        toggleConfirmationDialogOpen();
                      }}>
                      Stop check
                    </MenuItem>
                  )}
              </DataTableMenuCell>
            );
          },
          headerName: 'Actions'
        }
      ] as ColDef<DisbursementDto>[],
    [disbursementsQuery.data?.results]
  );

  const handleConfirm = useCallback(async () => {
    await initiateStopCheckDisbursementRequest.mutate(disbursementSelected);
    toggleConfirmationDialogOpen();
  }, [disbursementSelected]);

  return (
    <Card>
      <ActionConfirmationDialog
        disableConfirmButton={initiateStopCheckDisbursementRequest.isLoading}
        onCancel={toggleConfirmationDialogOpen}
        onConfirm={handleConfirm}
        open={isConfirmationDialogOpen}
      />
      <CardHeader
        data-testid='disbursements-header'
        loading={disbursementsQuery.data && disbursementsQuery.isFetching}
        title='Disbursements'>
        <FormControlLabel
          control={
            <Switch
              checked={isInactiveDisplay}
              onChange={(_event, newValue) => toggleInactiveDisplay(newValue)}
            />
          }
          data-testid='filter-inactive-disbursements-switch'
          label='Display inactive disbursements'
        />
      </CardHeader>
      <CardContent
        disablePadding
        overlayLoading={
          disbursementsQuery.isInitialLoading ||
          initiateStopCheckDisbursementRequest.isLoading
        }>
        <DataTable
          columnDefs={columnDefs}
          data-testid='data-disbursements-table'
          emptyPlaceholderComponent={
            <Stack
              alignItems='center'
              data-testid='no-data-disbursements-table'
              justifyContent='center'
              sx={{ height: '100%' }}>
              <CardPlaceholder
                icon={<SearchIcon fontSize='inherit' />}
                subtitle='No results found.'
              />
            </Stack>
          }
          onPageChanged={handleDisbursmentsPageChange}
          onPageSizeChanged={handleDisbursmentsPageSizeChange}
          onSortChanged={handleDisbursmentsSortChange}
          page={urlState.page}
          pageSize={urlState.pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          pagination
          rowData={disbursementsQuery.data?.results}
        />
      </CardContent>
    </Card>
  );
};
